import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { formatApiError } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Check, Rocket, Clock, Mail, PartyPopper, X } from "lucide-react";
import { toast } from "sonner";
import { useLocation, useNavigate } from "react-router-dom";
import { LAUNCH_DATE, markLaunchDismissed, pad, useCountdown } from "@/lib/launch";
import ShareLaunchCard from "@/components/marketing/ShareLaunchCard";

const PACKAGES = [
  { id: "spark", name: "Spark", price: "Free · 14-day trial", audience: "Solo",
    features: ["1 user · web-only", "9 priority websites", "Basic Focus timeline", "Refocus widget"] },
  { id: "focus-web", name: "Focus Web", price: "$14 / mo", audience: "Solo · web only",
    features: ["1 user · 9 websites", "Full FACE event log", "Weekly reports", "Chrome/Firefox extension"] },
  { id: "focus", name: "Focus", price: "$23 / mo", audience: "Solo · web + local apps", featured: true,
    features: ["1 user · 9 sites + 3 local apps", "Everything in Focus Web", "Local-app tracking", "Priority hierarchy"] },
  { id: "sync-web", name: "Sync Web", price: "$18 / user / mo", audience: "Teams 3–24 · web",
    features: ["3–24 users · web only", "Aggregate team analytics", "S-Tier hierarchy approval", "GDPR-compliant reporting"] },
  { id: "sync", name: "Sync", price: "$28 / user / mo", audience: "Teams 3–24 · full",
    features: ["3–24 users · web + 5 apps", "Everything in Sync Web", "Workspace dashboards", "Domain verification"] },
  { id: "momentum", name: "Momentum", price: "$38 / user / mo", audience: "Teams 25–100",
    features: ["25–100 users · 20 sites + 9 apps", "Advanced admin controls", "Custom org tiers", "Audit log export"] },
  { id: "command", name: "Command", price: "Custom", audience: "Enterprise · 101+ users",
    features: ["Unlimited seats", "Everything in Momentum", "SAML/SSO (roadmap)", "Dedicated onboarding"] },
];

export default function LaunchWaitlistModal({ onClose }) {
  const nav = useNavigate();
  const location = useLocation();
  const { days, hours, minutes, seconds } = useCountdown(LAUNCH_DATE);
  const [form, setForm] = useState({ email: "", name: "", package_id: "focus", seats: 1, notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const selectedPkg = useMemo(() => PACKAGES.find((p) => p.id === form.package_id), [form.package_id]);

  const close = () => {
    markLaunchDismissed();
    if (onClose) return onClose();
    if (location.key !== "default") nav(-1);
    else nav("/", { replace: true });
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape" || document.querySelector("[data-radix-select-viewport]")) return;
      close();
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email.trim()) {
      toast.error("Please enter your email.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/waitlist/signup", {
        email: form.email.trim(),
        name: form.name.trim() || null,
        package_id: form.package_id,
        seats: Math.max(1, parseInt(form.seats || 1, 10)),
        notes: form.notes.trim() || null,
      });
      setSubmitted(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not sign you up. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-start sm:items-center justify-center bg-[#0F1524]/70 backdrop-blur-sm p-4 overflow-y-auto"
      data-testid="waitlist-modal"
      role="dialog"
      aria-modal="true"
    >
      <div className="relative bg-white rounded-2xl border border-[#E8ECF2] shadow-[0_30px_80px_rgba(0,0,0,0.35)] max-w-3xl w-full my-4 sm:my-8">
        <button
          type="button"
          onClick={close}
          aria-label="Close"
          data-testid="waitlist-close"
          className="fixed sm:absolute top-4 right-4 z-[110] h-10 w-10 sm:h-9 sm:w-9 rounded-full bg-white sm:bg-[#F8F9FC] border border-[#E8ECF2] text-[#1A1A2E] sm:text-[#555568] shadow-md sm:shadow-none hover:text-[#1A1A2E] hover:bg-[#E8ECF2] flex items-center justify-center transition-colors"
        >
          <X size={18} className="sm:hidden" />
          <X size={16} className="hidden sm:block" />
        </button>

        {submitted ? (
          <ThankYou email={form.email} onClose={close} />
        ) : (
          <div className="p-8 md:p-10">
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-3">
                <Rocket size={14} className="text-[#2C6BAC]" />
                <p className="text-[11px] uppercase tracking-[0.22em] text-[#2C6BAC] font-semibold">Live in</p>
              </div>
              <div className="grid grid-cols-4 gap-3" data-testid="waitlist-countdown">
                <CountdownCell label="Days" value={days} />
                <CountdownCell label="Hours" value={hours} />
                <CountdownCell label="Minutes" value={minutes} />
                <CountdownCell label="Seconds" value={seconds} pulse />
              </div>
              <p className="mt-2 text-[11px] text-[#555568] flex items-center gap-1.5">
                <Clock size={11} /> Counting down to 00:00 UTC on 1 November 2026
              </p>
            </div>

            <h2 className="font-display text-3xl md:text-4xl tracking-tighter text-[#1A1A2E]">
              WYLO goes live on <span className="text-[#1A3A5C]">1 November 2026.</span>
            </h2>
            <p className="text-sm text-[#555568] mt-3 leading-relaxed">
              We're building WYLO carefully - employee-first, privacy-first, GDPR- and EU AI Act-ready. Join the waitlist and we'll email you the moment your seats are ready.
            </p>

            <form onSubmit={submit} className="mt-6 space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="wl-email">Email</Label>
                  <Input id="wl-email" type="email" required value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="you@company.com" data-testid="waitlist-email" className="mt-1" />
                </div>
                <div>
                  <Label htmlFor="wl-name">Name (optional)</Label>
                  <Input id="wl-name" value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Chrys Mason" data-testid="waitlist-name" className="mt-1" />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-2">
                  <Label>Package you're interested in</Label>
                  <Select value={form.package_id} onValueChange={(v) => setForm({ ...form, package_id: v })}>
                    <SelectTrigger className="mt-1" data-testid="waitlist-package-trigger">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="z-[200]" position="popper" data-testid="waitlist-packages">
                      {PACKAGES.map((p) => (
                        <SelectItem key={p.id} value={p.id} data-testid={`waitlist-pkg-${p.id}`}>
                          <span className="font-semibold">{p.name}</span>
                          <span className="text-[#555568]"> · {p.price} · {p.audience}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="wl-seats">Seats</Label>
                  <Input id="wl-seats" type="number" min={1} max={10000} value={form.seats}
                    onChange={(e) => setForm({ ...form, seats: e.target.value })}
                    data-testid="waitlist-seats" className="mt-1" />
                </div>
              </div>

              {selectedPkg && (
                <div className="rounded-md bg-[#F8F9FC] border border-[#E8ECF2] p-4" data-testid="waitlist-pkg-detail">
                  <p className="text-xs uppercase tracking-[0.18em] text-[#555568] font-semibold mb-2">
                    Included in {selectedPkg.name} <span className="normal-case tracking-normal text-[#1A3A5C]">· {selectedPkg.price}</span>
                  </p>
                  <ul className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
                    {selectedPkg.features.map((f, i) => (
                      <li key={i} className="text-sm text-[#1A1A2E] flex items-start gap-2">
                        <Check size={14} className="text-[#1D9E75] mt-0.5 flex-shrink-0" /> {f}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div>
                <Label htmlFor="wl-notes">Anything else (optional)</Label>
                <Textarea id="wl-notes" rows={2} value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="Team size, use case, timeline…" data-testid="waitlist-notes" className="mt-1" />
              </div>

              <div className="flex flex-col-reverse md:flex-row md:items-center md:justify-between gap-3 pt-2">
                <p className="text-xs text-[#555568] flex items-center gap-2">
                  <Mail size={12} /> Sent to <a href="mailto:support@usewylo.com" className="text-[#2C6BAC] hover:underline">support@usewylo.com</a>. No spam. Unsubscribe any time.
                </p>
                <div className="flex gap-2">
                  <Button type="button" variant="ghost" onClick={close} className="h-11 text-[#555568]" data-testid="waitlist-skip">
                    Not now
                  </Button>
                  <Button type="submit" disabled={submitting}
                    className="bg-[#2C6BAC] hover:bg-[#1A3A5C] text-white h-11 px-6" data-testid="waitlist-submit">
                    {submitting ? "Submitting…" : "Notify me at launch"}
                  </Button>
                </div>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}

function CountdownCell({ label, value, pulse }) {
  return (
    <div className="rounded-md bg-gradient-to-b from-[#1A3A5C] to-[#0F2540] text-white text-center py-3 shadow-inner">
      <p key={value}
        className={`font-display font-bold text-2xl md:text-3xl tabular-nums ${pulse ? "animate-[cd-pulse_1s_ease-out]" : ""}`}
        data-testid={`waitlist-cd-${label.toLowerCase()}`}>
        {pad(value)}
      </p>
      <p className="text-[10px] uppercase tracking-[0.2em] text-[#E8ECF2]/80">{label}</p>
    </div>
  );
}

function ThankYou({ email, onClose }) {
  return (
    <div className="p-8 md:p-12 text-center" data-testid="waitlist-thankyou">
      <div className="h-16 w-16 rounded-full bg-[#1D9E75]/15 text-[#1D9E75] mx-auto flex items-center justify-center">
        <PartyPopper size={28} />
      </div>
      <h2 className="mt-5 font-display text-3xl md:text-4xl tracking-tighter text-[#1A1A2E]">You're on the list.</h2>
      <p className="mt-3 text-sm text-[#555568] leading-relaxed max-w-md mx-auto">
        Thank you for supporting WYLO. We've notified{" "}
        <a href="mailto:support@usewylo.com" className="font-mono text-[#2C6BAC] hover:underline">support@usewylo.com</a> and will email{" "}
        <span className="font-mono text-[#1A1A2E]">{email}</span> the moment we go live on 1 November 2026.
      </p>
      <ShareLaunchCard />
      <Button onClick={onClose} variant="outline" className="mt-8 border-[#1A3A5C]" data-testid="waitlist-thankyou-close">
        Continue
      </Button>
    </div>
  );
}
